import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RolesService {
  private http = inject(HttpClient);

  // Mirrors legacy application_configuration mappings
  private apiServer = environment.authenticationServer;
  private authServer = environment.authenticationServer;
  private entityService = environment.entityService;

  // --- Entity & Module Lookups ---

  GetAllEntities(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.entityService}/api/entity`))
      .then(response => response.data || response);
  }

  GetAllModules(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.entityService}/api/package/modules`))
      .then(response => response.data || response);
  }

  // --- Role CRUD ---

  GetAccessRoles(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/accessrole`));
  }

  CreateRole(role: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/authorization/accessrole`, role));
  }

  UpdateRole(role: any): Promise<any> {
    return firstValueFrom(this.http.put<any>(`${this.apiServer}/api/authorization/role-update/${role.id}`, role));
  }

  CloneRole(role: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/authorization/clonerole`, role));
  }

  DeleteRole(role: any): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`${this.apiServer}/api/authorization/accessrole/${role.id}`));
  }

  // --- User-Role Mappings ---

  GetUserAccessRoles(role_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/userrole/role_id/${role_id}`))
      .then(response => response);
  }

  InsertUserRole(roleDetails: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/authorization/userrole`, roleDetails));
  }

  DeleteUserRole(user_role: any): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`${this.apiServer}/api/authorization/userrole/${user_role.id}`));
  }

  // --- Role Permissions ---

  GetRolePermission(role_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/accessroleandpermission/role_id/${role_id}`))
      .then(response => response);
  }

  GetAccessPermissions(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/authorization/accesspermissions`));
  }

  CreateRolePermission(rolePermission: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/authorization/rolepermission`, rolePermission));
  }

  InsertRolePermission(permissionDetails: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/authorization/accessrolepermission`, permissionDetails));
  }

  DeleteRolePermission(role_permission_id: string | number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`${this.apiServer}/api/authorization/accessrolepermission/${role_permission_id}`));
  }

  // --- Access Modules ---

  GetAccessModulesByRoleId(roleId: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/access/role/${roleId}/module`))
      .then(response => response);
  }

  InsertAccessModule(accessModuleDetails: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/access/role/module`, accessModuleDetails));
  }

  // --- Cloud Cart Permissions ---

  GetAccessRoleCart(role_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/access/cart/role/${role_id}`))
      .then(response => response);
  }

  GetAccessCartPermissions(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/access/cart/permissions`))
      .then(response => response);
  }

  InsertCartRolePermission(role_permission_cart: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/access/cart`, role_permission_cart))
      .then(response => response);
  }

  DeleteCartRolePermission(role_permission_cart: any): Promise<any> {
    return firstValueFrom(
      this.http.delete<any>(
        `${this.apiServer}/api/access/cart/CloudcartRolePermission/${role_permission_cart.permission_id}/${role_permission_cart.role_id}`
      )
    ).then(response => response);
  }

  // --- Features ---

  FetchAllFeatures(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/feature/features`))
      .then(response => response);
  }

  FetchFeatureRoles(role_id: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.apiServer}/api/feature/role/${role_id}/feature/access`))
      .then(response => response);
  }

  CreateFeatureRoles(featureRoleObject: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.apiServer}/api/feature/role/feature`, featureRoleObject))
      .then(response => response);
  }

  // --- Workbench ---

  GetWorkbenchPermissionsByRole(roleId: string | number): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.authServer}/api/access/workbench/role/${roleId}`))
      .then(response => response);
  }

  UpsertWorkbenchAccess(workbenchObject: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`${this.authServer}/api/access/workbench/`, workbenchObject))
      .then(response => response);
  }
}
